import {
  type HeaderType,
  type PathLookupSetupType,
  pathlookupSetupTypes,
} from '../pathLookup/PacketHeader';

export const protocolEdgeTypes = [
  'dgw',
  'dgwIpv6',
  'ebgp',
  'eigrp',
  'fex',
  'ibgp',
  'isis',
  'ldp',
  'ospf',
  'ospfv3',
  'pim',
  'rib',
  'ribIpv6',
  'rip',
  'rsvp',
  'stp',
  'stpi',
  'stpi-blocked',
  'vxlan',
  'wired',
  'wireless',
  'xdp',
  'ipsec',
  'manualLinkL1',
  'manualLinkL2',
  'sd-wan',
  'cloud-peering',
  'cloud-gateway',
] as const;
export const protocolEdgeTypeSet = new Set<string>(protocolEdgeTypes);
export type ProtocolEdgeType = (typeof protocolEdgeTypes)[number];

export const pathLookupEdgeTypes = pathlookupSetupTypes;
export const pathLookupEdgeTypeSet = new Set<string>(pathLookupEdgeTypes);
export type PathLookupEdgeType = PathLookupSetupType;

export type EdgeType = HeaderType | ProtocolEdgeType;

export const edgeCenterLabels = ['protocol', 'subnet', 'vlanId'] as const;
export const edgeLinecapLabels = [
  'area',
  'as',
  'cost',
  'intName',
  'intDescription',
  'ipAddress',
  'media',
  'prefix',
  'vrf',
] as const;

export type EdgeCenterLabel = (typeof edgeCenterLabels)[number];
export type EdgeLinecapLabel = (typeof edgeLinecapLabels)[number];

export type ProtocolLabels = {
  center: EdgeCenterLabel[];
  linecap: EdgeLinecapLabel[];
};

type ProtocolStyle = {
  color: number;
  dasharray?: number[];
};

export interface EdgeAttribute {
  allowInGraphSettings: boolean;
  defaultStyle: ProtocolStyle;
  labels: ProtocolLabels;
  name: string;
}

export type ProtocolEdgeAttributes = {
  [edgeName in ProtocolEdgeType]: EdgeAttribute;
};

export type PathLookupEdgeAttributes = {
  [edgeName in PathLookupSetupType]: EdgeAttribute;
};

export const protocolLabels: (EdgeCenterLabel | EdgeLinecapLabel)[] = [
  'area',
  'as',
  'cost',
  'intName',
  'ipAddress',
  'media',
  'prefix',
  'protocol',
  'subnet',
];

export const protocolEdgeAttributes: ProtocolEdgeAttributes = {
  dgw: {
    allowInGraphSettings: true,
    name: 'DGW',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  dgwIpv6: {
    allowInGraphSettings: true,
    name: 'DGW IPv6',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  ebgp: {
    allowInGraphSettings: true,
    name: 'eBGP',
    defaultStyle: {
      color: 0x009688,
    },
    labels: {
      center: ['protocol'],
      linecap: [
        'as',
        'ipAddress',
        'prefix',
        'vrf',
        'intName',
        'intDescription',
      ],
    },
  },
  eigrp: {
    allowInGraphSettings: true,
    name: 'EIGRP',
    defaultStyle: {
      color: 0x00bcd4,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['as', 'intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  fex: {
    allowInGraphSettings: true,
    name: 'FEX',
    defaultStyle: {
      color: 0xa0a0a0,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription'],
    },
  },
  ibgp: {
    allowInGraphSettings: true,
    name: 'iBGP',
    defaultStyle: {
      color: 0x009688,
    },
    labels: {
      center: ['protocol'],
      linecap: [
        'as',
        'ipAddress',
        'prefix',
        'vrf',
        'intName',
        'intDescription',
      ],
    },
  },
  ipsec: {
    allowInGraphSettings: true,
    name: 'IPSec',
    defaultStyle: {
      color: 0x009688,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  isis: {
    allowInGraphSettings: true,
    name: 'IS-IS',
    defaultStyle: {
      color: 0xcddc39,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  ldp: {
    allowInGraphSettings: true,
    name: 'LDP',
    defaultStyle: {
      color: 0xff9800,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  manualLinkL1: {
    allowInGraphSettings: true,
    name: 'Manual Link',
    defaultStyle: {
      color: 0x109688,
      dasharray: [4, 1],
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription'],
    },
  },
  manualLinkL2: {
    allowInGraphSettings: true,
    name: 'Manual Link',
    defaultStyle: {
      color: 0x109688,
      dasharray: [4, 1],
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription'],
    },
  },
  'sd-wan': {
    allowInGraphSettings: true,
    name: 'SD-WAN',
    defaultStyle: {
      color: 0xd4b524,
      dasharray: [4, 1],
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription'],
    },
  },
  'cloud-peering': {
    allowInGraphSettings: true,
    name: 'Cloud Peering',
    defaultStyle: {
      color: 0x4db8ff,
    },
    labels: {
      center: ['protocol'],
      linecap: [],
    },
  },
  'cloud-gateway': {
    allowInGraphSettings: true,
    name: 'Cloud Gateway',
    defaultStyle: {
      color: 0x0288d1,
    },
    labels: {
      center: ['protocol'],
      linecap: [],
    },
  },
  ospf: {
    allowInGraphSettings: true,
    name: 'OSPF',
    defaultStyle: {
      color: 0xffd600,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: [
        'area',
        'cost',
        'intName',
        'intDescription',
        'ipAddress',
        'vrf',
      ],
    },
  },
  ospfv3: {
    allowInGraphSettings: true,
    name: 'OSPFv3',
    defaultStyle: {
      color: 0xffd600,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: [
        'area',
        'cost',
        'intName',
        'intDescription',
        'ipAddress',
        'vrf',
      ],
    },
  },
  pim: {
    allowInGraphSettings: true,
    name: 'PIM',
    defaultStyle: {
      color: 0x9d744d,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  rib: {
    allowInGraphSettings: true,
    name: 'RIB',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  ribIpv6: {
    allowInGraphSettings: true,
    name: 'RIB IPv6',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  rip: {
    allowInGraphSettings: true,
    name: 'RIP',
    defaultStyle: {
      color: 0xa1887f,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  rsvp: {
    allowInGraphSettings: true,
    name: 'RSVP',
    defaultStyle: {
      color: 0x2693ff,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  stp: {
    allowInGraphSettings: true,
    name: 'STP',
    defaultStyle: {
      color: 0x99ccff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription', 'media'],
    },
  },
  stpi: {
    allowInGraphSettings: false,
    name: 'STPi',
    defaultStyle: {
      color: 0x99ccff,
    },
    labels: {
      center: ['vlanId'],
      linecap: ['intName'],
    },
  },
  'stpi-blocked': {
    allowInGraphSettings: false,
    name: 'STPi',
    defaultStyle: {
      color: 0xcccccc,
    },
    labels: {
      center: ['vlanId'],
      linecap: ['intName'],
    },
  },
  vxlan: {
    allowInGraphSettings: true,
    name: 'VXLAN',
    defaultStyle: {
      color: 0xff9999,
    },
    labels: {
      center: ['protocol', 'subnet'],
      linecap: ['intName', 'intDescription', 'ipAddress', 'vrf'],
    },
  },
  wired: {
    allowInGraphSettings: true,
    name: 'Wired',
    defaultStyle: {
      color: 0xbe93ff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription'],
    },
  },
  wireless: {
    allowInGraphSettings: true,
    name: 'Wireless',
    defaultStyle: {
      color: 0xbe93ff,
      dasharray: [4, 1],
    },
    labels: {
      center: ['protocol'],
      linecap: [],
    },
  },
  xdp: {
    allowInGraphSettings: true,
    name: 'xDP',
    defaultStyle: {
      color: 0x66ff66,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName', 'intDescription', 'media'],
    },
  },
};

export const pathLookupEdgeAttributes: PathLookupEdgeAttributes = {
  aws: {
    allowInGraphSettings: true,
    name: 'AWS',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  capwap: {
    allowInGraphSettings: true,
    name: 'CAPWAP',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  esp: {
    allowInGraphSettings: true,
    name: 'ESP',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  ethernet: {
    allowInGraphSettings: true,
    name: 'Ethernet',
    defaultStyle: {
      color: 0x7aa3cc,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  ethernetVlan: {
    allowInGraphSettings: true,
    name: 'Ethernet VLAN',
    defaultStyle: {
      color: 0x99ccff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  fp: {
    allowInGraphSettings: true,
    name: 'FabricPath',
    defaultStyle: {
      color: 0xff9800,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  gre: {
    allowInGraphSettings: true,
    name: 'GRE',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  ip: {
    allowInGraphSettings: true,
    name: 'IP',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  ipv6: {
    allowInGraphSettings: true,
    name: 'IPv6',
    defaultStyle: {
      color: 0xff99ff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  'meraki-vpn': {
    allowInGraphSettings: true,
    name: 'Meraki VPN',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  mpls: {
    allowInGraphSettings: true,
    name: 'MPLS',
    defaultStyle: {
      color: 0xff9800,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  spbmBackbone: {
    allowInGraphSettings: true,
    // The encapsulation is PBB / MAC-in-MAC (IEEE 802.1ah); SPBM (802.1aq) is
    // the control plane that uses it. Name the edge after the encapsulation.
    name: 'PBB (MAC-in-MAC)',
    defaultStyle: {
      color: 0x9c6ade,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  tcp: {
    allowInGraphSettings: false,
    name: 'TCP',
    defaultStyle: {
      color: 0xff9999,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  udp: {
    allowInGraphSettings: false,
    name: 'UDP',
    defaultStyle: {
      color: 0x66b2ff,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  vcmp: {
    allowInGraphSettings: true,
    name: 'VCMP',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  vxlan: {
    allowInGraphSettings: true,
    name: 'VXLAN',
    defaultStyle: {
      color: 0xd4b524,
    },
    labels: {
      center: ['protocol'],
      linecap: ['intName'],
    },
  },
  loop: {
    allowInGraphSettings: false,
    name: 'Loop Detection',
    defaultStyle: {
      color: 0xde5257,
    },
    labels: {
      center: [],
      linecap: [],
    },
  },
};

export const EDGE_LABEL_FONT = 'OpenSans-Regular';
export const EDGE_LABEL_FONT_SIZE = 12;
